import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { BulkUpdateService } from '../../../../service/bulk-update.service';
import {EventEmitterService} from '../../../../service/event-emitter.service';
import {Subscription} from 'rxjs';
import {DialogService} from 'primeng/dynamicdialog';
import { PreviewCampaignBulkUpdateComponent } from './preview-campaigns-bulk-update/preview-campaign-bulk-update.component';
import { BulkUpdateAccountFilterComponent } from './bulk-update-account-filter/bulk-update-account-filter.component';

@Component({
    selector: 'app-media-ad-copy-bulk-update',
    templateUrl: './media-ad-copy-bulk-update.component.html',
    styleUrls: ['./media-ad-copy-bulk-update.component.scss'],
    providers: [DialogService],
    standalone: false
})

export class MediaAdCopyBulkUpdateComponent implements OnInit, OnDestroy {

  private readonly requiredHeaders = ['Ad group'];

  private readonly optionalHeaders = {
    headlines: Array.from({length: 15}, (_, i) => `Headline ${i + 1}`),
    descriptions: Array.from({length: 4}, (_, i) => `Description ${i + 1}`),
    headlinePositions: Array.from({length: 15}, (_, i) => `Headline ${i + 1} Position`),
    descriptionPositions: Array.from({length: 4}, (_, i) => `Description ${i + 1} Position`),
    paths: ['Path 1', 'Path 2'],
    urls: ['Final URL', 'Mobile final URL', 'Tracking template', 'Final URL suffix'],
    other: ['Custom parameter']
  };
  @ViewChild('fileInput', { static: false })
  private fileInput!: ElementRef<HTMLInputElement>;

  bulkUpdateForm: FormGroup;
  isUploading: boolean = false;
  selectedFile: File | null = null;
  appliedFilters: { accountNames: string[]; accountIds: number[] } = { accountNames: [], accountIds: [] };
  private eventSubscription: Subscription;

  // Static strategy options including special 'Expression Match'
  strategyOptions: { label: string, value: string }[] = [
    { label: 'Search - Branded', value: 'Branded' },
    { label: 'Search - Dealer/Geo', value: 'Dealer/Geo' },
    { label: 'Search - New Model', value: 'New Sales (Model)' },
    { label: 'Search - Used Vehicle', value: ' Used Sales (Vehicle)' },
    { label: 'Expression Match', value: 'Expression Match' },
  ];

  // Label used in logic to detect expression mode
  expressionMatchLabel: string = 'Expression Match';

  @ViewChild(BulkUpdateAccountFilterComponent, { static: false })
  private accountFilterComp: BulkUpdateAccountFilterComponent;
  constructor(
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private bulkUpdateService: BulkUpdateService,
    private eventEmitterService: EventEmitterService,
    private dialogService: DialogService
  ) { }

  // Helper: selector is valid when a non-expression strategy is chosen OR when 'Expression Match' with a non-empty expression
  get selectorValid(): boolean {
    if (!this.bulkUpdateForm) return false;
    const strategy = this.bulkUpdateForm.get('strategy')?.value as string | null;
    const expr = this.bulkUpdateForm.get('expression')?.value as string | null;
    const isExpressionMode = strategy === this.expressionMatchLabel;
    const hasExpr = !!(expr && expr.trim().length > 0);
    const hasNonExpressionStrategy = !!(strategy && strategy.trim().length > 0 && strategy !== this.expressionMatchLabel);
    return hasNonExpressionStrategy || (isExpressionMode && hasExpr);
  }

  // Disable logic for Submit (Execute) button: requires accounts, valid selector, description, and CSV selected
  get isSubmitDisabled(): boolean {
    const hasAccount = (this.appliedFilters?.accountIds?.length || 0) > 0;
    const descriptionValid = this.bulkUpdateForm?.get('description')?.valid === true;
    const hasFile = !!this.selectedFile;
    return !hasAccount || !this.selectorValid || !descriptionValid || !hasFile || this.isUploading;
  }

  ngOnInit(): void {
    this.initForm();
    this.eventSubscription = this.eventEmitterService.event$.subscribe((event) => {
      if (event && event.name === 'mediaAdCopyFiltersChanged') {
        this.appliedFilters = event.payload || { accountNames: [], accountIds: [] };
      }
    });
  }

  /**
   * Initialize the form with required fields
   */
  private initForm(): void {
    this.bulkUpdateForm = this.formBuilder.group(
      {
        // New controls
        strategy: [{ value: null, disabled: false }],
        expression: [{ value: '', disabled: true }],

        // Backward-compat field expected by BE for now; keep in sync with expression
        campaignExpression: [''],

        description: ['', Validators.required]
      },
      { validators: this.xorStrategyExpressionValidator }
    );

    // Keep campaignExpression in sync with expression for BE compatibility
    this.bulkUpdateForm.get('expression')?.valueChanges.subscribe((val: string) => {
      const expr = (val ?? '').toString();
      const campaignExprCtrl = this.bulkUpdateForm.get('campaignExpression');
      if (campaignExprCtrl?.value !== expr) {
        campaignExprCtrl?.setValue(expr, { emitEvent: false });
      }
    });

    // When strategy changes, enable expression only for 'Expression Match'
    this.bulkUpdateForm.get('strategy')?.valueChanges.subscribe((val: string | null) => {
      const expressionCtrl = this.bulkUpdateForm.get('expression');
      const campaignExprCtrl = this.bulkUpdateForm.get('campaignExpression');
      const isExpressionMode = (val === this.expressionMatchLabel);
      if (isExpressionMode) {
        expressionCtrl?.enable({ emitEvent: false });
      } else {
        // Clear and disable expression when not in expression mode
        if (expressionCtrl?.value) {
          expressionCtrl.setValue('', { emitEvent: false });
        }
        if (campaignExprCtrl?.value) {
          campaignExprCtrl.setValue('', { emitEvent: false });
        }
        expressionCtrl?.disable({ emitEvent: false });
      }
    });
  }

  // Group-level validator: valid if either a non-expression strategy is selected OR 'Expression Match' with a non-empty expression
  private xorStrategyExpressionValidator = (group: AbstractControl): ValidationErrors | null => {
    let result = null;
    if ((group instanceof FormGroup)) {
      const strategy = group.get('strategy')?.value as string | null;
      const expr = group.get('expression')?.value as string | null;
      const isExpressionMode = strategy === this.expressionMatchLabel;
      const hasExpr = !!(expr && expr.trim().length > 0);
      const hasNonExpressionStrategy = !!(strategy && strategy.trim().length > 0 && strategy !== this.expressionMatchLabel);
      if (!(hasNonExpressionStrategy || (isExpressionMode && hasExpr))) {
        result = { xorStrategyExpression: true };
      } else {
        result = null;
      }
    }

    return result;
  };

  /**
   * Triggers file picker
   */
  triggerFilePicker(): void {
    this.fileInput.nativeElement.click();
  }

  /**
   * Handle file selection for CSV upload
   * @param event File input event
   */
  onFileSelect(event: any): void {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      this.selectedFile = null;

      if (!this.isValidCSVFile(file)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Invalid File',
          detail: 'Please upload a valid CSV file'
        });
      } else {
        this.validateCSVHeaders(file).then(result => {
          if (result.isValid) {
            this.selectedFile = file;
            this.messageService.add({
              severity: 'info',
              summary: 'File Selected',
              detail: `Selected file: ${file.name}`
            });
          } else {
            const missingHeadersText = result.missingRequired.join(', ');
            this.messageService.add({
              severity: 'error',
              summary: 'Invalid CSV Format',
              detail: `Missing required headers: ${missingHeadersText}`
            });
          }
        }).catch(() => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to validate CSV file'
          });
        });
      }
    }
  }

  /**
   * Check if the file is a valid CSV file
   * @param file File to validate
   * @returns boolean indicating if file is valid
   */
  private isValidCSVFile(file: File): boolean {
    return file.name.endsWith('.csv') || file.type === 'text/csv';
  }

  /**
   * Validates CSV headers against required and optional headers
   * @param file The CSV file to validate
   * @returns Promise with validation result
   */
  private validateCSVHeaders(file: File): Promise<{
    isValid: boolean,
    missingRequired: string[],
    foundOptional: string[]
  }> {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const contents = e.target.result;
        const lines = contents.split('\n');

        if (lines.length === 0) {
          resolve({
            isValid: false,
            missingRequired: this.requiredHeaders,
            foundOptional: []
          });
          return;
        }

        // Get headers from first line and trim whitespace
        const headers = lines[0].split(',').map(header => header.trim());

        const missingRequired = this.requiredHeaders.filter(
          requiredHeader => !headers.includes(requiredHeader)
        );

        const allOptionalHeaders = [
          ...this.optionalHeaders.headlines,
          ...this.optionalHeaders.descriptions,
          ...this.optionalHeaders.headlinePositions,
          ...this.optionalHeaders.descriptionPositions,
          ...this.optionalHeaders.paths,
          ...this.optionalHeaders.urls,
          ...this.optionalHeaders.other
        ];

        const foundOptional = headers.filter(
          header => allOptionalHeaders.includes(header)
        );

        resolve({
          isValid: missingRequired.length === 0,
          missingRequired,
          foundOptional
        });
      };

      reader.onerror = () => {
        resolve({
          isValid: false,
          missingRequired: this.requiredHeaders,
          foundOptional: []
        });
      };

      reader.readAsText(file);
    });
  }

  /**
   * Handle form submission
   */
  onSubmit(): void {
    const filtersChangedSincePreview = this.accountFilterComp && this.accountFilterComp.hasFormChanges === true;
    if (this.bulkUpdateForm.valid && this.selectedFile && this.appliedFilters.accountIds.length > 0 && filtersChangedSincePreview === false) {
      this.isUploading = true;

      this.bulkUpdateService.parseCSVFile(this.selectedFile)
        .then((adCopyData) => {
          const strategyVal = this.bulkUpdateForm.get('strategy').value as string | null;
          const expression = this.bulkUpdateForm.get('expression').value as string;
          const excludedAccountIds = this.accountFilterComp.excludedAccountIds;
          const finalAccountIds = this.appliedFilters.accountIds.filter(
            (id) => !excludedAccountIds.find((item) => item === id)
          );
          const requestData: any = {
            accountIds: finalAccountIds,
            campaignType: 'Search',
            campaignStrategy: strategyVal,
            expression: expression || undefined,
            adCopyData: adCopyData,
            description: this.bulkUpdateForm.get('description').value
          }

          this.bulkUpdateService.sendBulkUpdateRequest(requestData)
            .subscribe(
              response => {
                this.isUploading = false;
                this.messageService.add({
                  severity: 'success',
                  summary: 'Success',
                  detail: 'Bulk update request submitted successfully'
                });
              },
              error => {
                this.isUploading = false;
                this.messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Failed to submit bulk update request'
                });
              }
            );
        })
        .catch(error => {
          this.isUploading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to parse CSV file'
          });
        });
    } else if (!this.selectedFile) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please select a CSV file'
      });
    } else if (this.appliedFilters.accountIds.length === 0 || filtersChangedSincePreview) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Preview Required',
        detail: 'Please preview the account filters before submitting.'
      });
    } else {
      this.bulkUpdateForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields'
      });
    }
  }

  ngOnDestroy(): void {
    if (this.eventSubscription) {
      this.eventSubscription.unsubscribe();
    }
  }

  /**
   * Open modal to select a single account and run a preview for it
   */
  onPreviewSingleAccount(): void {
    const accounts = this.appliedFilters.accountIds.map((id, idx) => ({
      id, name: this.appliedFilters.accountNames[idx] || String(id)
    }));

    const strategy = this.bulkUpdateForm.get('strategy')?.value ?? null;
    const expression = this.bulkUpdateForm.get('expression')?.value ?? '';

    const ref = this.dialogService.open(PreviewCampaignBulkUpdateComponent, {
      header: 'Preview Media Ad Copy Bulk Update',
      width: '70vw',
      modal: true,
      closable: true,
      data: {
        appliedFilters: this.appliedFilters,
        strategy,
        expression
      }
    });

    ref.onClose.subscribe((result) => {
      let selectedAccountId: number;
      if (result?.selectedAccountId) {
        selectedAccountId = result?.selectedAccountId;
      } else {
        selectedAccountId = accounts[0]?.id;
      }

      if (!selectedAccountId) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No account selected for preview' });
        return;
      }
    });
  }

  /**
   * Method to check if a account filter is invalid or empty
   */
  invalidAccountFilter(accountFilter) {
    return accountFilter.filterRows.length === 0 || (accountFilter.filterRows.length > 0 && !accountFilter.hasValidFilters());
  }

  /**
   * Method to check if the preview campaign expression button should be disabled
   */
  previewCampaignsDisabled(accountFilter){
    const filtersChangedSincePreview = this.accountFilterComp && this.accountFilterComp.hasFormChanges === true;
    return this.appliedFilters.accountIds.length === 0
      || this.invalidAccountFilter(accountFilter)
      || this.bulkUpdateForm.get('campaignExpression').invalid
      || this.isUploading
      || !this.selectorValid
      || filtersChangedSincePreview;
  }

  /**
   * Method to get disabled tooltip text for the preview campaign expression button
   */
  getDisabledToolTip(accountFilter) {
    let result = '';
    if (this.previewCampaignsDisabled(accountFilter)) {
      result = 'Enter a campaign strategy and preview account filters with at least one account.';
    }
    return result;
  }
}
